import React, { useState } from 'react';

import { Contract, PostboxDocument } from 'cpes-common';
import { Layout, Button } from '@vwfs-bronson/bronson-react';
import { useTranslation } from 'react-i18next';

import { PostboxDocumentType, resolveInvoiceDocumentType } from '../helper/DocumentType';
import { AllTimeframes, Timeframe } from '../helper/Timeframe';
import { DocumentTypeFilter } from './filters/DocumentTypeFilter';
import { TimeframeFilter } from './filters/TimeFrameFilter';
import { ContractFilter } from './filters/ContractFilter';
import { PostboxTableUi } from './table/PostboxTableUi';
import { useAnalyticsPageViewTracker } from '@cp-shared-19/frontend-ui';

export const PostboxUi: React.FC<{
    postboxDocuments?: PostboxDocument[];
    contracts?: Contract[];
    hasInvoiceConsent: boolean;
    hasSignUpCheckError: boolean;
    initialFilterToInvoices?: boolean;
    initialTimeframeFilter?: Timeframe | AllTimeframes;
    defaultTimeframe?: Timeframe | AllTimeframes;
    showContractNumberFilter?: boolean;
    enablePagination?: boolean;
}> = ({
    postboxDocuments,
    contracts,
    hasInvoiceConsent,
    hasSignUpCheckError,
    initialFilterToInvoices,
    initialTimeframeFilter,
    defaultTimeframe,
    showContractNumberFilter,
    enablePagination,
}) => {
    const { t } = useTranslation('postbox');
    useAnalyticsPageViewTracker('postbox', !!(contracts && postboxDocuments));

    const all = 'ALL';

    const initialDocumentTypeFilter = initialFilterToInvoices
        ? resolveInvoiceDocumentType(postboxDocuments || [])
        : undefined;

    const [currentDocumentTypeFilter, setCurrentDocumentTypeFilter] = useState<PostboxDocumentType>(
        initialDocumentTypeFilter || all,
    );
    const [currentTimeFrameFilter, setCurrentTimeFrameFilter] = useState<Timeframe | AllTimeframes>(
        initialTimeframeFilter || defaultTimeframe || all,
    );
    const [currentContractFilter, setCurrentContractFilter] = useState<string>(all);

    const [resetKey, setResetKey] = useState<number>(0);

    if (!contracts || !postboxDocuments) {
        return null;
    }

    // Set session storage consent on failing sign-up check
    let sessionConfirmedInvoice = false;
    if (hasSignUpCheckError) {
        sessionStorage.setItem('sessionConfirmedInvoices', 'Y');
    }

    // if consent is not given by check-up check if session consent is given
    if (!hasInvoiceConsent) {
        sessionConfirmedInvoice = sessionStorage.getItem('sessionConfirmedInvoices')
            ? sessionStorage.getItem('sessionConfirmedInvoices') === 'Y'
            : false;
        hasInvoiceConsent = sessionConfirmedInvoice;
    }

    const hasOperativeLeaseContracts = contracts
        ? contracts.some(contract => contract.isOperatingLeaseContract)
        : false;

    const postboxDocumentsListIsEmpty = postboxDocuments.length === 0;

    const documentTypeFilterChange = (value: PostboxDocumentType) => {
        setCurrentDocumentTypeFilter(value);
    };

    const timeframeFilterChange = (value: Timeframe | AllTimeframes) => {
        setCurrentTimeFrameFilter(value);
    };

    const contractFilterChange = (value: string) => {
        setCurrentContractFilter(value);
    };

    const resetAllFilters = (): void => {
        /**
         *  Pattern taken from
         *  https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
         */
        setResetKey(resetKey + 1);

        setCurrentDocumentTypeFilter(all);
        setCurrentTimeFrameFilter(defaultTimeframe || all);
        setCurrentContractFilter(all);
    };

    return (
        <>
            {postboxDocumentsListIsEmpty ? null : (
                <>
                    <Layout>
                        <Layout.Item default={showContractNumberFilter ? '1/4' : '1/3'} xs={'1/1'} m={'1/1'}>
                            <DocumentTypeFilter
                                key={resetKey}
                                postboxDocuments={postboxDocuments}
                                onChange={documentTypeFilterChange}
                                initialValue={initialDocumentTypeFilter}
                            />
                        </Layout.Item>

                        <Layout.Item default={showContractNumberFilter ? '1/4' : '1/3'} xs={'1/1'} m={'1/2'}>
                            <TimeframeFilter
                                key={resetKey}
                                postboxDocuments={postboxDocuments}
                                onChange={timeframeFilterChange}
                                initialValue={initialTimeframeFilter || defaultTimeframe}
                            />
                        </Layout.Item>

                        {showContractNumberFilter && (
                            <Layout.Item default={'1/2'} xs={'1/1'} m={'1/1'}>
                                <ContractFilter
                                    key={resetKey}
                                    contracts={contracts}
                                    selectedContract={all}
                                    onChange={contractFilterChange}
                                />
                            </Layout.Item>
                        )}
                    </Layout>
                    <Layout className={'u-text-right u-mt-small'}>
                        <Layout.Item className={'u-mt-none'}>
                            <Button
                                element="a"
                                link
                                onClick={resetAllFilters}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        resetAllFilters();
                                    }
                                }}
                                testId={'ResetButtonLink'}
                                icon={'semantic-reload'}
                                tabIndex={0}
                            >
                                {t('reset-button')}
                            </Button>
                        </Layout.Item>
                    </Layout>
                </>
            )}

            <Layout>
                <Layout.Item default={'1/1'}>
                    <PostboxTableUi
                        postboxDocuments={postboxDocuments}
                        activeDocumentTypeFilter={currentDocumentTypeFilter}
                        activeTimeframeFilter={currentTimeFrameFilter}
                        activeContractFilter={currentContractFilter}
                        hasInvoiceConsent={hasInvoiceConsent}
                        hasOperativeLeaseContracts={hasOperativeLeaseContracts}
                        enablePagination={enablePagination}
                    />
                </Layout.Item>
            </Layout>
        </>
    );
};
